/**
 * app-utility-context.js
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import AlertDialog from 'components/alert-dialog';


export const AppUtilityContext = React.createContext();


export const AppUtilityProvider = (props) => {
  const {
    children,
  } = props;
  const [state, setState] = React.useState({
    alert: null,
    confirm: null,
  });


  // generic close for given key
  const onClose = (key) => {
    setState(p => ({
      ...p,
      [key]: {
        ...p[key],
        isOpen: false
      }
    }));
    setTimeout(() => {
      setState(p => ({
        ...p,
        [key]: null
      }));
    }, 500);
  }


  // info alert dialog
  const alertInfo = React.useCallback(({
    text,
    title = 'Info',
    titleStyle = 'info',
    titleIcon = 'info',
    onOk = null,
  }) => {
    setState(p => ({
      ...p,
      alert: {
        isOpen: true,
        onOk,
        props: {
          text,
          title,
          titleStyle,
          titleIcon
        }
      }
    }));
  }, []);


  // warning alert dialog
  const alertWarning = React.useCallback(({
    text,
    title = 'Warning',
    titleStyle = 'warning',
    titleIcon = 'warning',
    onOk = null,
  }) => {
    setState(p => ({
      ...p,
      alert: {
        isOpen: true,
        onOk,
        props: {
          text,
          title,
          titleStyle,
          titleIcon,
        }
      }
    }));
  }, []);


  // error alert dialog
  const alertError = React.useCallback(({
    text,
    title = 'Error',
    titleStyle = 'error',
    titleIcon = 'warning-circle',
    onOk = null,
  }) => {
    setState(p => ({
      ...p,
      alert: {
        isOpen: true,
        onOk,
        props: {
          text,
          title,
          titleStyle,
          titleIcon,
        }
      }
    }));
  }, []);


  // promise-based confirm dialog
  const confirm = React.useCallback(({
    text,
    title = 'Confirm',
    okLabel = 'OK',
    cancelLabel = 'Cancel',
    titleColor = null,
    actions,
  }) => {
    return new Promise((resolve, reject) => {
      const defaultActions = [{
        value: false,
        props: {
          color: 'inherit',
          children: cancelLabel,
        }
      }, {
        value: true,
        props: {
          autoFocus: true,
          color: 'primary',
          children: okLabel,
        }
      }];
      setState(p => ({
        ...p,
        confirm: {
          isOpen: true,
          resolve,
          title,
          titleColor,
          text,
          actions: actions ?? defaultActions,
        }
      }));
    });
  }, []);


  // TODO: add other conveniences in here:
  // - global spinner


  // state value
  const value = React.useMemo(() => ({
    alertInfo,
    alertWarning,
    alertError,
    confirm,
  }), [
    alertInfo,
    alertWarning,
    alertError,
    confirm,
  ]);

  
  return (<>
    <AppUtilityContext.Provider 
      value={value}
      children={children}
    />

    {/* ALERT */}
    {state.alert &&
    <AlertDialog
      open={Boolean(state.alert?.isOpen)}
      ok={() => {
        if (state.alert.onOk) {
          state.alert.onOk();
        }
        onClose('alert');
      }}
      {...state.alert?.props ?? {}}
    />}

    {/* CONFIRM */}
    {state.confirm &&
    <Dialog
      open={state.confirm.isOpen}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle 
        id="confirm-dialog-title"
        sx={{ backgroundColor: state.confirm.titleColor }} 
        children={state.confirm.title || 'Confirm'}
      />
      {state.confirm.text && (
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">{state.confirm.text}</DialogContentText>
        </DialogContent>
      )}
      <DialogActions>
        {state.confirm.actions.map((action, i) => (
          <Button
            key={i}
            style={{ lineHeight: 'inherit', marginLeft: i > 0 ? 12 : 0 }}
            {...action.props}
            onClick={() => {
              state.confirm.resolve(action.value);
              onClose('confirm');
            }}
          />
        ))}
      </DialogActions>
    </Dialog>}
  </>);
}